import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Link } from 'react-router-dom';
import {
  Mic,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Target,
  Award,
  ChevronRight,
  Loader2,
  Volume2,
  MessageSquare,
  Sparkles,
  AlertCircle
} from 'lucide-react';

const SpeechProfileCard = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/speech/profile');
      setHasProfile(response.data.has_profile);
      setProfile(response.data.profile);
    } catch (error) {
      console.error('Failed to fetch speech profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-emerald-500" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Improving';
      case 'declining':
        return 'Needs focus';
      default:
        return 'Stable';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!hasProfile) {
    return (
      <Card className="h-full bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-violet-500/10">
              <Mic className="h-6 w-6 text-violet-500" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold">Build Your Speech Profile</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Complete voice sessions to start tracking your communication patterns and improvements.
              </p>
              <Link to="/simulation">
                <Button size="sm" className="mt-4 gap-2">
                  <Volume2 className="h-4 w-4" />
                  Start a Voice Session
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to="/speech-profile" data-testid="speech-profile-card">
      <Card className="h-full hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/10 to-purple-500/10">
                <Mic className="h-5 w-5 text-violet-500" />
              </div>
              <div>
                <CardTitle className="text-base">Voice Profile</CardTitle>
                <CardDescription className="text-xs">
                  {profile.total_sessions} sessions analyzed
                </CardDescription>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Clarity Score - Main Metric */}
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-muted/20"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(profile.avg_clarity_score / 100) * 176} 176`}
                  className={getScoreColor(profile.avg_clarity_score)}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-lg font-bold ${getScoreColor(profile.avg_clarity_score)}`}>
                  {Math.round(profile.avg_clarity_score)}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Clarity Score</p>
              <div className="flex items-center gap-2 mt-1">
                {getTrendIcon(profile.clarity_trend)}
                <span className="text-xs text-muted-foreground">
                  {getTrendLabel(profile.clarity_trend)}
                </span>
              </div>
              {profile.recent_improvement_pct > 0 && (
                <Badge variant="secondary" className="mt-2 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  +{profile.recent_improvement_pct}% improved
                </Badge>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold">{profile.avg_filler_word_rate.toFixed(1)}%</p>
              <p className="text-[10px] text-muted-foreground">Filler Rate</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold">{Math.round(profile.avg_speaking_pace)}</p>
              <p className="text-[10px] text-muted-foreground">WPM</p>
            </div>
          </div>

          {/* Current Focus */}
          {profile.current_focus_area && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Target className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300 truncate">
                Focus: {profile.current_focus_area}
              </p>
            </div>
          )}

          {/* Activity Badge */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{profile.sessions_this_week} sessions this week</span>
            {profile.best_clarity_score > 0 && (
              <span className="flex items-center gap-1">
                <Award className="h-3 w-3 text-amber-500" />
                Best: {Math.round(profile.best_clarity_score)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default SpeechProfileCard;
